import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogScroll,
  DialogTitle,
  DialogTitleActions,
  Spinner,
  Type,
} from "discover.medical.react-component-library";
import { CenterContentFlex, StyledIcon } from "./BookmarksDialog.styled";
import { useDispatch, useStore } from "../../store";
import { getBookmarks } from "../../store/actions/bookmark";
import BookmarkItemRow from "./BookmarkItemRow";
import ErrorBoundary from "../ErrorBoundary";

export default function BookmarksDialog(props) {
  const { isOpen, setIsOpen } = props;
  const history = useHistory();
  const store = useStore();
  const dispatch = useDispatch();
  const { isFetching, isError, bookmarks = [] } = store?.bookmark || {};

  function closeModal() {
    setIsOpen(false);
    const { pathname, search, hash, state } = history.location;
    const rebuiltPath = `${pathname}${search}${hash}`;
    history.replace(rebuiltPath, {
      ...state,
      openBookmarksModal: false,
    });
  }

  useEffect(() => {
    if (isOpen) {
      getBookmarks(dispatch);
    }
  }, [dispatch, isOpen]);

  const renderBookmarks = useCallback(() => {
    if (isFetching) {
      return (
        <CenterContentFlex
          container
          justify={{ content: "center" }}
          align={{ item: "center" }}
        >
          <Spinner data-testid="bookmarks-spinner" size="md" />
        </CenterContentFlex>
      );
    } else if (!bookmarks.length) {
      return (
        <CenterContentFlex
          container
          justify={{ content: "center" }}
          align={{ item: "center" }}
        >
          <Type data-testid="bookmarks-no-content">
            You currently have no bookmarks. To bookmark, use the{" "}
            <StyledIcon
              icon={{ glyph: "bookmark", size: "md", color: "gray600" }}
            />{" "}
            on content pages.
          </Type>
        </CenterContentFlex>
      );
    } else {
      return (
        <>
          {bookmarks.map((bookmark, bookmarkIndex) => (
            <BookmarkItemRow
              key={bookmark?.metadata?.slug}
              bookmark={bookmark}
              bookmarkIndex={bookmarkIndex}
            />
          ))}
        </>
      );
    }
  }, [isFetching, bookmarks]);

  return (
    <ErrorBoundary team="cd.mergeconflict">
      <Dialog
        background="black"
        data-testid="bookmarks-dialog"
        isOpen={isOpen}
        maxWidth="md"
        onRequestClose={closeModal}
        showCloseButton
      >
        <DialogTitle
          borderBottomColor="gray300"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
        >
          <Type
            element="h2"
            fontFamily="secondary"
            fontSize="lg"
            color="gray800"
          >
            My Bookmarks
          </Type>
          <DialogTitleActions onRequestClose={closeModal} />
        </DialogTitle>
        <DialogScroll style={{ maxHeight: "23rem" }}>
          <Card>
            <CardContent>
              <div data-testid="bookmarks-container">
                {isError ? (
                  <CenterContentFlex
                    container
                    justify={{ content: "center" }}
                    align={{ item: "center" }}
                  >
                    <Alert type="error" data-testid="bookmarks-error">
                      Unexpected server error occurred. Please try again later.
                    </Alert>
                  </CenterContentFlex>
                ) : (
                  renderBookmarks()
                )}
              </div>
            </CardContent>
          </Card>
        </DialogScroll>
      </Dialog>
    </ErrorBoundary>
  );
}

BookmarksDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};
